import React from 'react';
import { Section } from '../ui/Section';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
function TrustpilotStar({
  filled = true
}: {
  filled?: boolean;
}) {
  return <div className={`w-7 h-7 flex items-center justify-center ${filled ? 'bg-[#00B67A]' : 'bg-[#dcdce6]'}`} data-id="element-315">
      <svg viewBox="0 0 24 24" className="w-4 h-4 text-white fill-current" data-id="element-316">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-317" />
      </svg>
    </div>;
}
function StarRating({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-0.5" data-id="element-318">
      {[1, 2, 3, 4, 5].map(i => <TrustpilotStar key={i} filled={i <= rating} data-id="element-319" />)}
    </div>;
}
const testimonials = [{
  rating: 5,
  title: 'Mi colesterol bajó de 255 a 198',
  quote: 'Tenía colesterol 255 y el doctor ya quería darme estatinas. Empecé con el protocolo boldo-diente león + arroz rojo. Mes y medio después: 198. Mi doctor no lo podía creer.',
  author: 'Roberto M.',
  details: '52 años',
  date: 'Hace 2 semanas',
  initial: 'R'
}, {
  rating: 5,
  title: 'Ya no me tapo la cara con maquillaje',
  quote: 'El acné hormonal me destruyó la autoestima a los 34. Mascarilla cúrcuma + té ortiga semana 2: ya no me tapo la cara con maquillaje.',
  author: 'Laura V.',
  details: '34 años',
  date: 'Hace 3 semanas',
  initial: 'L'
}, {
  rating: 5,
  title: 'Mi esposo y yo lo usamos juntos',
  quote: 'Mi esposo y yo los dos usamos el botiquín. Él para espalda de obra, yo para rodillas e insomnio. Primer mes: él duerme 7 horas seguidas, yo camino al mercado sin cojear.',
  author: 'María T.',
  details: '41 años',
  date: 'Hace 1 mes',
  initial: 'M'
}, {
  rating: 5,
  title: 'Glicemia de 118 a 94 en 2 meses',
  quote: 'Era escéptico. Soy hombre, 45. Glicemia 118 en ayunas. Canela + ajo + ejercicio 15min. Mes 2: glicemia 94. Ya no me da sueño post-almuerzo.',
  author: 'Carlos P.',
  details: '45 años',
  date: 'Hace 1 mes',
  initial: 'C'
}];
export function Testimonials() {
  const totalReviews = 847;
  const avgRating = 4.9;
  return <div className="bg-[#f7f7fa] py-8" data-id="element-320">
      <Section className="!bg-transparent" data-id="element-321">
        {/* Trustpilot-style Header */}
        <div className="text-center mb-6" data-id="element-322">
          <div className="inline-flex flex-col items-center gap-2 mb-4" data-id="element-323">
            {/* Rating Summary */}
            <div className="flex items-center gap-3" data-id="element-324">
              <StarRating rating={5} data-id="element-325" />
            </div>
            <p className="text-[#2C1810] text-lg" data-id="element-326">
              Calificado como{' '}
              <span className="font-bold text-[#00B67A]" data-id="element-327">Excelente</span>
            </p>
            <p className="text-sm text-gray-500" data-id="element-328">
              {avgRating} de 5 basado en{' '}
              <span className="font-semibold text-[#2C1810] underline" data-id="element-329">
                {totalReviews.toLocaleString()} reseñas
              </span>
            </p>
          </div>

          {/* Rating Distribution Bar */}
          <div className="max-w-xs mx-auto space-y-1.5" data-id="element-330">
            {[{
            stars: 5,
            pct: 89
          }, {
            stars: 4,
            pct: 8
          }, {
            stars: 3,
            pct: 2
          }, {
            stars: 2,
            pct: 1
          }, {
            stars: 1,
            pct: 0
          }].map(row => <div key={row.stars} className="flex items-center gap-2 text-xs" data-id="element-331">
                <span className="w-3 text-gray-500 font-medium text-right" data-id="element-332">
                  {row.stars}
                </span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-333">
                  <motion.div initial={{
                width: 0
              }} whileInView={{
                width: `${row.pct}%`
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.2
              }} className="h-full bg-[#00B67A] rounded-full" data-id="element-334" />
                </div>
                <span className="w-8 text-gray-400 text-right" data-id="element-335">{row.pct}%</span>
              </div>)}
          </div>
        </div>

        {/* Review Cards */}
        <div className="grid md:grid-cols-2 gap-4" data-id="element-336">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm" data-id="element-337">
              {/* Stars */}
              <div className="mb-3" data-id="element-338">
                <StarRating rating={t.rating} data-id="element-339" />
              </div>

              {/* Title */}
              <h4 className="font-bold text-[#2C1810] text-base mb-2" data-id="element-340">
                {t.title}
              </h4>

              {/* Review Text */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4" data-id="element-341">
                {t.quote}
              </p>

              {/* Author Row */}
              <div className="flex items-center justify-between border-t border-gray-100 pt-3" data-id="element-342">
                <div className="flex items-center gap-3" data-id="element-343">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-600" data-id="element-344">
                    {t.initial}
                  </div>
                  <div data-id="element-345">
                    <p className="text-sm font-semibold text-[#2C1810]" data-id="element-346">
                      {t.author}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-347">{t.details}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1" data-id="element-348">
                  <span className="text-[10px] bg-[#00B67A]/10 text-[#00B67A] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1" data-id="element-349">
                    <CheckCircle2 className="w-3 h-3" data-id="element-350" /> Verificada
                  </span>
                  <span className="text-[10px] text-gray-400" data-id="element-351">{t.date}</span>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Bottom Trust Line */}
        <div className="text-center mt-5" data-id="element-352">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500" data-id="element-353">
            <div className="flex gap-0.5" data-id="element-354">
              {[1, 2, 3, 4, 5].map(i => <div key={i} className="w-4 h-4 bg-[#00B67A] flex items-center justify-center" data-id="element-355">
                  <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white fill-current" data-id="element-356">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-357" />
                  </svg>
                </div>)}
            </div>
            <span data-id="element-358">Reseñas verificadas de compradores reales</span>
          </div>
        </div>
      </Section>
    </div>;
}